import React, { useState, useEffect } from 'react';
import { workLogAdminAPI, adminAPI } from '../../services/api';
import './WorkLogManagement.css';
import { formatGermanDateTime } from '../../utils/timezone';
import { 
  Monitor, 
  Smartphone, 
  Edit3, 
  HelpCircle,
  User,
  Trash2,
  Edit,
  Search,
  Plus,
  Clock,
  Bot,
  AlertCircle,
  Lightbulb
} from 'lucide-react';
import { WorkLogEditModal } from './WorkLogEditModal';

interface WorkLog {
  id: number;
  startTime: string;
  endTime: string | null;
  description: string | null;
  totalMinutes: number | null;
  breakMinutes: number | null;
  logMethod: string;
  logMethod2: string | null;
  user: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdBy?: {
    firstName: string;
    lastName: string;
  };
}

interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

export const WorkLogManagement: React.FC = () => {
  //const [workLogs, setWorkLogs] = useState<WorkLog[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Initialize with current month dates
  const now = new Date();
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  
  const [filters, setFilters] = useState({
    userId: '',
    startDate: firstDayOfMonth.toISOString().split('T')[0],
    endDate: lastDayOfMonth.toISOString().split('T')[0],
    sortBy: 'startTime',
    sortOrder: 'desc'
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });

  const [showWorkLogModal, setShowWorkLogModal] = useState(false);
  const [editingWorkLog, setEditingWorkLog] = useState<any>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedUserName, setSelectedUserName] = useState<string>('');

  const [stats, setStats] = useState({
    totalMinutes: 0,
    totalBreakMinutes: 0,
    totalRecords: 0
  });

  // ersetzt bisherigen workLogs state durch rawWorkLogs + abgeleitete gefilterte Liste
  const [rawWorkLogs, setRawWorkLogs] = useState<WorkLog[]>([]);
  //const [workLogsLoading, setWorkLogsLoading] = useState(false); // optional, falls getrennte Ladeanzeige nötig

  // Neuer Filter: 'any' | 'only' | 'exclude'
  const [autoLogoutFilter, setAutoLogoutFilter] = useState<'any' | 'only' | 'exclude'>('any');

  useEffect(() => {
    loadUsers();
    loadWorkLogs();
  }, [filters, pagination.page]);

  const loadUsers = async () => {
    try {
      const response = await adminAPI.getAllUsers({ limit: 1000 });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadWorkLogs = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        userId: filters.userId ? parseInt(filters.userId) : undefined,
        page: pagination.page,
        limit: pagination.limit
      };

      const response = await workLogAdminAPI.getWorkLogs(params);
      
      // Rohdaten speichern; Anzeige erfolgt gefiltert clientseitig
      setRawWorkLogs(response.data.workLogs);
      setStats({
        totalMinutes: response.data.totalMinutes,
        totalBreakMinutes: response.data.totalBreakMinutes,
        totalRecords: response.data.pagination.total
      });
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        pages: response.data.pagination.pages
      }));
    } catch (error) {
      console.error('Error loading work logs:', error);
      setMessage({ type: 'error', text: 'Fehler beim Laden der Arbeitslogs' });
    } finally {
      setLoading(false);
    }
  };

  // Clientseitige Erkennung ob ein Log ein "Auto-Logout" ist
  const isAutoLogout = (log: WorkLog) => {
    return !!(log.description && log.description.includes('Auto-Ausstempeln'));
  };
  
  // Gefilterte Liste für Anzeige (abhängig von autoLogoutFilter)
  const filteredWorkLogs = rawWorkLogs.filter(log => {
    if (autoLogoutFilter === 'any') return true;
    if (autoLogoutFilter === 'only') return isAutoLogout(log);
    if (autoLogoutFilter === 'exclude') return !isAutoLogout(log);
    return true;
  });

  const handleDelete = async (workLog: WorkLog) => {
    if (!confirm(`Arbeitslog von ${workLog.user.firstName} ${workLog.user.lastName} vom ${new Date(workLog.startTime).toLocaleDateString('de-DE')} wirklich löschen?`)) {
      return;
    }

    try {
      await workLogAdminAPI.deleteWorkLog(workLog.id);
      setMessage({ type: 'success', text: 'Arbeitslog erfolgreich gelöscht' });
      loadWorkLogs();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Fehler beim Löschen des Arbeitslogs'
      });
    }
  };

  const handleEdit = (workLog: WorkLog) => {
    setEditingWorkLog(workLog);
    setSelectedUserId(workLog.user.id);
    setSelectedDate(new Date(workLog.startTime).toISOString().split('T')[0]);
    setSelectedUserName(`${workLog.user.firstName} ${workLog.user.lastName}`);
    setShowWorkLogModal(true);
  };

  const handleRowClick = (workLog: WorkLog) => {
    handleEdit(workLog);
  };

  const handleCreate = () => {
    setEditingWorkLog(null);
    setSelectedUserId(null);
    setSelectedDate(new Date().toISOString().split('T')[0]);
    setSelectedUserName('');
    setShowWorkLogModal(true);
  };

  const handleWorkLogSuccess = async () => {
    await loadWorkLogs();
    setMessage({ type: 'success', text: 'Arbeitszeit erfolgreich gespeichert' });
    setTimeout(() => {
      setMessage({ type: '', text: '' });
    }, 3000);
  };

  const closeWorkLogModal = () => {
    setShowWorkLogModal(false);
    setEditingWorkLog(null);
    setSelectedUserId(null);
    setSelectedDate('');
    setSelectedUserName('');
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatDateTime = (dateString: string) => {
    return formatGermanDateTime(dateString);
  };

  const testAutoClockOut = async () => {
    try {
      const response = await fetch('/api/admin/work-sessions/debug', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Network response was not ok');
      }
      
      const data = await response.json();
      console.log('Work Sessions Debug:', data);
      
      const message = `
Debug Info:
Aktuelle Arbeitssitzungen: ${data.activeWorkSessions}
Aktuelle Zeit: ${new Date(data.currentTime).toLocaleString('de-DE')}

Sessions:
${data.sessions.map((s: any) => 
  `- ${s.user}: ${s.hoursWorked}h von ${s.maxHours}h (Auto Clock-Out: ${s.shouldAutoClockOut ? 'JA' : 'NEIN'})`
).join('\n')}
      `;
      
      alert(message);
    } catch (error) {
      console.error('Error checking work sessions:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
      setMessage({ type: 'error', text: `Fehler beim Überprüfen der Arbeitssitzungen: ${errorMessage}` });
    }
  };

  const triggerManualAutoClockOut = async () => {
    try {
      const response = await fetch('/api/admin/auto-clock-out', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Network response was not ok');
      }
      
      const data = await response.json();
      setMessage({ type: 'success', text: `Auto Clock-Out Test: ${data.message}` });

      loadWorkLogs();
    } catch (error) {
      console.error('Error triggering auto clock-out:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
      setMessage({ type: 'error', text: `Fehler beim Auto Clock-Out Test: ${errorMessage}` });
    }
  };

  const handleSort = (column: string) => {
    setFilters(prev => ({
      ...prev,
      sortBy: column,
      sortOrder: prev.sortBy === column && prev.sortOrder === 'desc' ? 'asc' : 'desc'
    }));
  };

  const getSortIcon = (column: string) => {
    if (filters.sortBy !== column) {
      return <span className="sort-icon">⇅</span>;
    }
    return filters.sortOrder === 'asc' 
      ? <span className="sort-icon active">↑</span>
      : <span className="sort-icon active">↓</span>;
  };

  return (
    <div className="worklog-management">
      <div className="worklog-header">
        <h2><Clock size={16} /> Arbeitszeit-Verwaltung</h2>
        <div className="header-actions">
          <button 
            className="btn primary"
            onClick={handleCreate}
          >
            <Plus size={16} /> Neuen Eintrag erstellen
          </button>
        </div>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
          <button 
            className="message-close" 
            onClick={() => setMessage({ type: '', text: '' })}
          >
            ×
          </button>
        </div>
      )}

      {/* Auto Clock-Out Test Section */}
      <div className="auto-clockout-section">
        <h3><Bot size={16} /> Auto Clock-Out System</h3>
        <p>Überwachung und Tests für das automatische Ausstempeln nach maximaler Arbeitszeit.</p>
        
        <div className="auto-clockout-actions">
          <button 
            className="btn secondary"
            onClick={testAutoClockOut}
            disabled={false}
            type="button"
          >
            <Search size={16} /> Arbeitssitzungen prüfen
          </button>
          <button 
            className="btn primary"
            onClick={triggerManualAutoClockOut}
            disabled={false}
            type="button"
          >
            <AlertCircle size={16} /> Auto Clock-Out manuell ausführen
          </button>
        </div>
        
        <div className="info-note">
          <small>
            <Lightbulb size={16} /> Das Auto Clock-Out System läuft alle 5 Minuten im Hintergrund. 
            Benutzer werden automatisch ausgestempelt, wenn ihre konfigurierte maximale Arbeitszeit erreicht wird.
          </small>
        </div>
      </div>

      {/* Statistics */}
      <div className="worklog-stats">
        <div className="stat-card">
          <h4>Gesamt Arbeitszeit</h4>
          <p className="stat-value">{formatDuration(stats.totalMinutes)}</p>
        </div>
        <div className="stat-card">
          <h4>Gesamt Pausen</h4>
          <p className="stat-value">{formatDuration(stats.totalBreakMinutes)}</p>
        </div>
        <div className="stat-card">
          <h4>Brutto Arbeitszeit</h4>
          <p className="stat-value">{formatDuration(stats.totalMinutes + stats.totalBreakMinutes)}</p>
        </div>
        <div className="stat-card">
          <h4>Anzahl Einträge</h4>
          <p className="stat-value">{stats.totalRecords}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="worklog-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>Mitarbeiter:</label>
            <select
              value={filters.userId}
              onChange={(e) => setFilters(prev => ({ ...prev, userId: e.target.value }))}
            >
              <option value="">Alle Mitarbeiter</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName}
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label>Auto-Logout:</label>
            <select
              value={autoLogoutFilter}
              onChange={(e) => setAutoLogoutFilter(e.target.value as any)}
            >
              <option value="any">Beliebig</option>
              <option value="only">Nur Auto-Logouts</option>
              <option value="exclude">Ohne Auto-Logouts</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>Von:</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
            />
          </div>
          
          <div className="filter-group">
            <label>Bis:</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
            />
          </div>
          
          <button 
            className="btn secondary"
            onClick={() => {
              setFilters(prev => ({ ...prev, userId: '', startDate: '', endDate: '' }));
              setAutoLogoutFilter('any');
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          >
            Filter zurücksetzen
          </button>
        </div>
      </div>

      {/* Work Logs Table */}
      {loading ? (
        <div className="loading">Lade Arbeitslogs...</div>
      ) : (
        <>
          <div className="worklog-table-container">
            <table className="worklog-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('userId')} className="sortable">
                    Mitarbeiter {getSortIcon('userId')}
                  </th>
                  <th onClick={() => handleSort('startTime')} className="sortable">
                    Startzeit {getSortIcon('startTime')}
                  </th>
                  <th onClick={() => handleSort('endTime')} className="sortable">
                    Endzeit {getSortIcon('endTime')}
                  </th>
                  <th onClick={() => handleSort('totalMinutes')} className="sortable">
                    Dauer (Netto) {getSortIcon('totalMinutes')}
                  </th>
                  <th onClick={() => handleSort('breakMinutes')} className="sortable">
                    Pause {getSortIcon('breakMinutes')}
                  </th>
                  <th>Brutto</th>
                  <th>Beschreibung</th>
                  <th>Methode</th>
                  <th>Erstellt von</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filteredWorkLogs.map((log) => (
                  <tr 
                    key={log.id} 
                    className={!log.endTime ? 'active-log' : ''}
                    onClick={() => handleRowClick(log)}
                    title="Klicken zum Bearbeiten"
                  >
                    <td>
                      <div className="user-info">
                        <strong>{log.user.firstName} {log.user.lastName}</strong>
                        <small>{log.user.email}</small>
                      </div>
                    </td>
                    <td>{formatDateTime(log.startTime)}</td>
                    <td>
                      {log.endTime ? (
                        <div className="end-time-info">
                          {formatDateTime(log.endTime)}
                          {log.description && log.description.includes('Auto-Ausstempeln') && (
                            <span className="auto-clockout-indicator" title="Automatisch ausgestempelt nach Erreichen der maximalen Arbeitszeit">
                              🤖 Auto
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="status-badge active">Läuft...</span>
                      )}
                    </td>
                    <td>
                      {log.totalMinutes ? (
                        <strong>{formatDuration(log.totalMinutes)}</strong>
                      ) : '-'}
                    </td>
                    <td>
                      {log.breakMinutes && log.breakMinutes > 0 ? (
                        <span className="break-time">{formatDuration(log.breakMinutes)}</span>
                      ) : '-'}
                    </td>
                    <td>
                      {log.totalMinutes ? (
                        formatDuration(log.totalMinutes + (log.breakMinutes || 0))
                      ) : '-'}
                    </td>
                    <td className="description-cell">
                      {log.description || '-'}
                    </td>
                    <td>
                      <div className="method-badges">
                        <span className={`method-badge ${log.logMethod.toLowerCase()}`}>
                          {log.logMethod === 'WEBSITE' ? (
                            <Monitor size={16} style={{ marginRight: '4px' }} />
                          ) : log.logMethod === 'NFC' ? (
                            <Smartphone size={16} style={{ marginRight: '4px' }} />
                          ) : log.logMethod === 'MANUAL_ADMIN' ? (
                            <Edit3 size={16} style={{ marginRight: '4px' }} />
                          ) : (
                            <HelpCircle size={16} style={{ marginRight: '4px' }} />
                          )} 
                          Ein: {log.logMethod.replace('_', ' ')}
                        </span>
                        {log.endTime && log.logMethod2 && (
                          <>
                            <span className="method-arrow">→</span>
                            <span className={`method-badge ${log.logMethod2.toLowerCase()}`}>
                              {log.logMethod2 === 'WEBSITE' ? (
                                <Monitor size={16} style={{ marginRight: '4px' }} />
                              ) : log.logMethod2 === 'NFC' ? (
                                <Smartphone size={16} style={{ marginRight: '4px' }} />
                              ) : log.logMethod2 === 'MANUAL_ADMIN' ? (
                                <Edit3 size={16} style={{ marginRight: '4px' }} />
                              ) : (
                                <HelpCircle size={16} style={{ marginRight: '4px' }} />
                              )} 
                              Aus: {log.logMethod2.replace('_', ' ')}
                            </span>
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      {log.createdBy ? (
                        <span className="created-by">
                          <User size={16} style={{ marginRight: '4px' }} />
                          {log.createdBy.firstName} {log.createdBy.lastName}
                        </span>
                      ) : (
                        <span className="self-created">Benutzer</span>
                      )}
                    </td>
                    <td className="actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn-icon edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(log);
                        }}
                        title="Bearbeiten"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        className="btn-icon delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(log);
                        }}
                        title="Löschen"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* WorkLog Edit/Create Modal */}
      {showWorkLogModal && (
        <WorkLogEditModal
          isOpen={showWorkLogModal}
          onClose={closeWorkLogModal}
          onSuccess={handleWorkLogSuccess}
          workLog={editingWorkLog}
          userId={selectedUserId || (users.length > 0 ? users[0].id : 0)}
          date={selectedDate}
          userName={selectedUserName || 'Mitarbeiter auswählen'}
        />
      )}
    </div>
  );
};